import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/base/ArticleCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import useSEOMeta from '@/hooks/useSEOMeta';
import { articles } from '@/mocks/articles';
import { categories, categoryMap } from '@/mocks/categories';

export default function Category() {
  const { categorySlug } = useParams<{ categorySlug: string }>();
  const [activeSlug, setActiveSlug] = useState(categorySlug || categories[0].slug);

  useEffect(() => {
    if (categorySlug && categoryMap[categorySlug]) {
      setActiveSlug(categorySlug);
    }
  }, [categorySlug]);

  const currentCategory = categoryMap[activeSlug] || categories[0];

  const filteredArticles = useMemo(
    () => articles.filter((a) => a.category === activeSlug),
    [activeSlug]
  );

  useSEOMeta({
    title: `${currentCategory.name} - 三上悠亚粉丝站`,
    description: currentCategory.description,
    keywords: `三上悠亚,${currentCategory.name},三上悠亚${currentCategory.name},三上悠亚粉丝站`,
    canonicalUrl: `https://cdrmjj.cn/category/${activeSlug}`,
    ogType: 'website',
  });

  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 三上悠亚粉丝站`,
    description: currentCategory.description,
    url: `https://cdrmjj.cn/category/${activeSlug}`,
    inLanguage: 'zh-CN',
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(collectionSchema)}</script>
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-24 pb-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: currentCategory.name },
              ]}
            />

            <div className="mt-6">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900">
                {currentCategory.name}
              </h1>
              <p className="mt-2 text-sm text-foreground-500">{currentCategory.description}</p>
            </div>

            {/* Category Tabs */}
            <div className="mt-8 flex items-center gap-2 overflow-x-auto pb-2 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveSlug(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-label whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-accent-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {/* Articles Grid */}
            {filteredArticles.length > 0 ? (
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredArticles.map((article) => (
                  <ArticleCard key={article.slug} article={article} />
                ))}
              </div>
            ) : (
              <div className="mt-16 text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="mt-4 text-foreground-500">该分类下暂无文章，敬请期待</p>
              </div>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}